import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";
import { useEscapeKey } from "@/hooks/useEscapeKey";

const inputCls = "mt-1 w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#FFC817]/50";
const labelCls = "text-[10px] uppercase tracking-wider text-[#A8A29E]";

// Phase 1 of the Operational Control Upgrade (2026-02): PM sees/edits the operational tier only;
// Founder/Partner see/edit operational + sensitive. This UI split is a convenience — the REAL
// boundary is server-side (modules/build.py::update_freelancer_profile()'s role-conditional
// whitelist rejects a forbidden field with 403 regardless of what this component ever renders).
const OPERATIONAL_FIELDS = [
  { key: "primary_role", label: "Primary Role", kind: "select", optionsKey: "primary_role" },
  { key: "secondary_roles", label: "Secondary Roles", kind: "multiselect", optionsKey: "secondary_roles" },
  { key: "city", label: "City", kind: "text" },
  { key: "country", label: "Country", kind: "text" },
  { key: "work_links", label: "Work Links", kind: "text" },
  { key: "website", label: "Website", kind: "text" },
  { key: "instagram", label: "Instagram", kind: "text" },
  { key: "youtube", label: "YouTube", kind: "text" },
  { key: "vimeo", label: "Vimeo", kind: "text" },
  { key: "behance", label: "Behance", kind: "text" },
  { key: "availability", label: "Availability", kind: "select", optionsKey: "availability" },
  { key: "internal_notes", label: "Internal Notes", kind: "textarea" },
];
const SENSITIVE_FIELDS = [
  { key: "agreement_status", label: "Verification Status", kind: "select", optionsKey: "agreement_status" },
  { key: "approved", label: "Approved", kind: "checkbox" },
  { key: "portal_access", label: "Portal Access", kind: "checkbox" },
  { key: "day_rate", label: "Day Rate", kind: "number" },
  { key: "half_day_rate", label: "Half-Day Rate", kind: "number" },
  { key: "negotiated_rates", label: "Negotiated Rates", kind: "textarea" },
  { key: "project_rates", label: "Project Rates", kind: "textarea" },
  // Test-account quarantine (2026-02): founder/partner-only, distinct from test_record (the
  // fully-hidden internal QA fixture — never editable, see build.py). A test_account is a REAL
  // functional account (e.g. njmforever@gmail.com) just excluded from directory/dashboard
  // counts by default.
  { key: "test_account", label: "Test Account", kind: "checkbox" },
];

function Field({ f, value, options, onChange }) {
  const testId = `edit-freelancer-${f.key}`;
  if (f.kind === "select") {
    return (
      <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">—</option>
        {(options?.[f.optionsKey] || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (f.kind === "multiselect") {
    const selected = value || [];
    return (
      <div className="mt-1 flex flex-wrap gap-2" data-testid={testId}>
        {(options?.[f.optionsKey] || []).map((o) => (
          <label key={o} className="inline-flex items-center gap-1.5 rounded-full border border-[#E7E5E4] px-2.5 py-1 text-xs cursor-pointer">
            <input
              type="checkbox" checked={selected.includes(o)} data-testid={`${testId}-${o}`}
              onChange={(e) => onChange(e.target.checked ? [...selected, o] : selected.filter((s) => s !== o))}
            />
            {o}
          </label>
        ))}
      </div>
    );
  }
  if (f.kind === "checkbox") {
    return (
      <div className="mt-1.5">
        <input type="checkbox" data-testid={testId} checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4" />
      </div>
    );
  }
  if (f.kind === "textarea") {
    return <textarea data-testid={testId} value={value || ""} rows={3} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
  }
  if (f.kind === "number") {
    return <input data-testid={testId} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className={inputCls} />;
  }
  return <input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
}

export function EditFreelancerProfileModal({ freelancer, onClose, onSaved }) {
  const { user } = useAuth();
  const canEditSensitive = user?.role === "founder" || user?.role === "partner";
  const fields = canEditSensitive ? [...OPERATIONAL_FIELDS, ...SENSITIVE_FIELDS] : OPERATIONAL_FIELDS;

  const [options, setOptions] = useState({});
  const [values, setValues] = useState(() => {
    const init = {};
    fields.forEach((f) => {
      // Duplicate Role display-time fix (2026-09): never pre-check a legacy row's Primary
      // Role as one of its own Secondary Roles in this multiselect.
      init[f.key] = f.key === "secondary_roles" ? (freelancer.secondary_roles || []).filter((r) => r !== freelancer.primary_role) : freelancer[f.key];
    });
    return init;
  });
  const [saving, setSaving] = useState(false);
  // Nikhil-approved follow-up (2026-10) to the Bavna Achayya onboarding_status investigation —
  // a dedicated, isolated "Mark as Onboarded"/"Clear" action, separate from the regular
  // patch-on-Save-Changes flow above, so it NEVER gets bundled with an in-progress edit to
  // approved/portal_access/verification status (each is its own PATCH call with nothing else
  // in the body). Mirrored into local state so the read-only display + button label update
  // immediately on success without waiting on the parent list to re-fetch.
  const [onboardingStatus, setOnboardingStatus] = useState(freelancer.onboarding_status);
  const [markingOnboarding, setMarkingOnboarding] = useState(false);
  useEscapeKey(onClose);

  useEffect(() => {
    api.get("/v1/build/freelancers/field-options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  const setField = (key, value) => setValues((v) => ({ ...v, [key]: value }));

  const markOnboarded = async () => {
    const next = onboardingStatus === "Onboarded" ? null : "Onboarded";
    const confirmed = window.confirm(
      next === "Onboarded"
        ? `Mark ${freelancer.name} as Onboarded? This does NOT send any email, create a Drive vault, or change Approved/Portal Access/Verification Status — use the Interview Decision flow for a full onboarding instead.`
        : `Clear ${freelancer.name}'s Onboarding Status back to not-onboarded? This does NOT change Approved/Portal Access/Verification Status.`
    );
    if (!confirmed) return;
    setMarkingOnboarding(true);
    try {
      await api.patch(`/v1/build/freelancers/${freelancer.id}`, { onboarding_status: next });
      setOnboardingStatus(next);
      toast.success(next === "Onboarded" ? "Marked as Onboarded" : "Onboarding Status cleared");
      onSaved?.({ ...freelancer, onboarding_status: next });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Part 9 Cursor review item 4 (2026-10): the button below is already disabled for this
      // exact case — this stays as a defense-in-depth fallback (e.g. a stale read, or a
      // second browser tab) so the raw backend error code never leaks to the user.
      if (detail?.code === "onboarding_status_already_set") {
        toast.error(`This profile already has an onboarding status (${detail.current}). Change it through the interview flow.`);
      } else {
        toast.error(detail?.code === "forbidden_field" ? "Not allowed to edit Onboarding Status" : (detail?.error || detail?.code || "Couldn't update Onboarding Status"));
      }
    } finally {
      setMarkingOnboarding(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      fields.forEach((f) => {
        if (values[f.key] !== freelancer[f.key]) patch[f.key] = values[f.key];
      });
      if (Object.keys(patch).length === 0) {
        toast.info("No changes to save");
        setSaving(false);
        return;
      }
      const r = await api.patch(`/v1/build/freelancers/${freelancer.id}`, patch);
      toast.success("Profile updated", { description: r.data.updated_fields.join(", ") });
      onSaved?.({ ...freelancer, ...patch });
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.code === "forbidden_field" ? `Not allowed to edit: ${detail.fields.join(", ")}` : (detail?.error || detail?.code || "Couldn't save changes"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="edit-freelancer-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="edit-freelancer-modal-backdrop" />
      <div className="relative w-full max-w-md h-full bg-[#FFFFFF] border-l border-[#E7E5E4] overflow-y-auto shadow-2xl animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between border-b border-[#E7E5E4] px-5 py-4 sticky top-0 bg-[#FFFFFF] z-10">
          <h2 className="font-editorial text-xl tracking-tight">Edit Freelancer Profile</h2>
          <button data-testid="edit-freelancer-modal-close" onClick={onClose} className="text-[#A8A29E] hover:text-[#1C1C1E]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[#A8A29E]">Operational</div>
          {OPERATIONAL_FIELDS.map((f) => (
            <div key={f.key}>
              <label className={labelCls}>{f.label}</label>
              <Field f={f} value={values[f.key]} options={options} onChange={(v) => setField(f.key, v)} />
            </div>
          ))}

          {canEditSensitive && (
            <>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[#A8A29E] pt-2 border-t border-[#E7E5E4]">
                Founder / Partner Only
              </div>
              {SENSITIVE_FIELDS.map((f) => (
                <div key={f.key} className={f.kind === "checkbox" ? "flex items-center justify-between" : ""}>
                  <label className={labelCls}>{f.label}</label>
                  <Field f={f} value={values[f.key]} options={options} onChange={(v) => setField(f.key, v)} />
                </div>
              ))}

              <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#E7E5E4]">
                <div>
                  <label className={labelCls}>Onboarding Status</label>
                  <div className="text-sm text-[#1C1C1E] mt-1" data-testid="edit-freelancer-onboarding-status-readonly">
                    {onboardingStatus || "Not onboarded"}
                  </div>
                  {/* Part 9 Cursor review item 4 (2026-10): mirrors the backend guardrail
                      (Part 8 item 8) — "Mark as Onboarded" can only direct-set when the
                      current status is empty/None; a non-empty, non-"Onboarded" status
                      (Applied/Selected/Interviewed/Declined/...) must go through the
                      Interview Decision flow instead. */}
                  {onboardingStatus && onboardingStatus !== "Onboarded" && (
                    <div className="text-[11px] text-[#A8A29E] mt-1 max-w-[220px]" data-testid="edit-freelancer-mark-onboarded-disabled-reason">
                      Already has a pipeline status ("{onboardingStatus}") — change it through the Interview Decision flow.
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  data-testid="edit-freelancer-mark-onboarded-btn"
                  onClick={markOnboarded}
                  disabled={markingOnboarding || (onboardingStatus && onboardingStatus !== "Onboarded")}
                  title={onboardingStatus && onboardingStatus !== "Onboarded" ? `Already has onboarding status "${onboardingStatus}" — change it through the Interview Decision flow instead.` : undefined}
                  className="shrink-0 rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs font-medium text-[#57534E] hover:border-[#FFC817] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:border-[#E7E5E4]"
                >
                  {markingOnboarding ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : (onboardingStatus === "Onboarded" ? "Clear Onboarding Status" : "Mark as Onboarded")}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-[#FFFFFF] border-t border-[#E7E5E4] px-5 py-4 flex justify-end gap-2">
          <button data-testid="edit-freelancer-modal-cancel" onClick={onClose} className="rounded-md border border-[#E7E5E4] px-4 py-2 text-sm text-[#57534E] hover:bg-black/[0.03]">
            Cancel
          </button>
          <button
            data-testid="edit-freelancer-modal-save" onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#FFC817] px-4 py-2 text-sm font-medium text-[#1C1C1E] hover:bg-[#FFC817]/90 disabled:opacity-60"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
